// =========================================================================
// Sign on the Ledger by cable, from the dashboard (17 Oct 2026).
//
// The operator's ask: "terminal Signer I would like to have it in my dashboard
// itself". So this sheet does what the signing page does, with the page's own
// code: fetch the card, decode and check it (sign-card.ts over
// cosign/src/verify.ts), show the same facts, hand the SAME bytes to the
// Ledger over WebHID (cosign/src/ledger.ts signOnLedger, with the signer the
// server named and the register's derivation to try first), and post the
// signature to the dashboard's route — which is the page's route's own
// implementation behind the operator's sign-in.
//
// The page's two controls hold here too. The recipient is placed against the
// list of destinations this browser confirmed (one per network, written only
// after a signature the server accepted), and a destination this browser has
// not seen is confirmed by TYPING the end of its address from a record that
// is not this screen. And the Ledger's own screen is the only display a
// compromised server cannot reach — the sheet says so, every time.
//
// Only where WebHID exists (Chromium on a computer). Elsewhere Approvals keeps
// the signing link and its QR code, as before.
//
// Screens' round 2 (23 Oct 2026): the card read is gated like the handoff's
// mint — only the latest opening's answer lands, a close or another card
// drops what is in flight — and the typed confirmation holds for the card
// and address it was typed for, nothing else (lib/custody-screen.ts).
// =========================================================================

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { X, ShieldAlert, ShieldCheck, Usb } from 'lucide-react';
import { StateLine, Pending, Label } from '../../components/kit';
import { custody as api, type SigningCard } from '../../lib/api';
import { ApiError } from '../../lib/errors';
import { fmtTime } from '../../lib/format';
import { groupDigits, KIND_WORD } from '../../lib/custody-format';
import {
  webHid, signOnLedger, buildNetwork, loadConfirmed, confirmDestination, parseDestination, CONFIRM_TAIL,
  type LedgerStep, type EntryKind,
} from '../../lib/ledger-cable';
import { checkCard, anchorSentence, confirmInstruction, type CardCheck } from './sign-card';
import { signatureRefusal, signedSentence, refusalIsDone } from './refusals';
import { makeMintGate, confirmationHolds } from '../../lib/custody-screen';
import { FIELD } from './field';

type Phase =
  | { at: 'loading' }
  | { at: 'refused'; text: string; done?: boolean }
  | { at: 'checked'; check: CardCheck }
  | { at: 'signing'; check: CardCheck; step: LedgerStep | null; text: string }
  | { at: 'signed'; check: CardCheck; text: string }
  | { at: 'failed'; check: CardCheck; text: string; remedy: string | null; askedDevice: boolean };

const textOf = (err: unknown): string => String((err as { message?: unknown } | null)?.message ?? err);
const remedyOf = (err: unknown): string | null => {
  const remedy = (err as { remedy?: unknown } | null)?.remedy;
  return typeof remedy === 'string' && remedy ? remedy : null;
};

/**
 * A card the server will no longer serve, as a sentence — the same three
 * answers whether the sheet is opening or re-reading at the tap (on 18 Oct
 * 2026 the tap's re-read showed "approval_expired", the code, when the
 * card's half hour ran out under the operator's hand).
 */
function cardRefusal(err: unknown): { text: string; done: boolean } | null {
  if (!(err instanceof ApiError)) return null;
  const reason = typeof err.body.reason === 'string' ? err.body.reason : null;
  switch (reason) {
    case 'approval_signed':
      return { done: true, text: 'Already signed. The signature is recorded and the desk sends it to the network — nothing more to do here.' };
    case 'approval_expired':
      return { done: false, text: 'The signing window for this transaction has closed, and nothing was sent. A fresh one appears in the list while an approval window is open.' };
    case 'approval_superseded':
      return { done: false, text: 'This transaction was replaced by a newer build. Open the newer one from the list.' };
    case 'window_closed':
      return { done: false, text: 'No approval window is open. Open one, then sign.' };
    default:
      return null;
  }
}

export function CableSign({ open, onClose, txid, title }: {
  open: boolean;
  onClose: () => void;
  txid: string;
  title: string;
}) {
  const qc = useQueryClient();
  const network = useMemo(() => buildNetwork(), []);
  // Which opening of the sheet a card read, or a signature, belongs to.
  const gate = useMemo(() => makeMintGate(), []);
  const [phase, setPhase] = useState<Phase>({ at: 'loading' });
  const [typed, setTyped] = useState('');
  /** Set once the ending has been typed, for THIS card and address: what goes onto this browser's list if the signature is accepted. */
  const [typedFor, setTypedFor] = useState<{ txid: string; address: string; partyName: string; kind: EntryKind; layer?: string | null } | null>(null);

  const load = useCallback(async () => {
    setPhase({ at: 'loading' });
    setTyped('');
    setTypedFor(null);
    if (!network) {
      setPhase({ at: 'refused', text: 'This build of the app names no TRON network, so it cannot sign. Report this — the app was set up wrongly.' });
      return;
    }
    try {
      // Only the latest opening's card lands: an answer for a sheet since
      // closed, or for another card, is dropped — its facts never show under
      // this title, and its failure is not this card's.
      const read = await gate.open(() => api.card(txid));
      if (read.stale) return;
      setPhase({ at: 'checked', check: checkCard(read.value, { txid, network, now: Date.now(), confirmed: loadConfirmed(network) }) });
    } catch (err) {
      const refusal = cardRefusal(err);
      if (refusal) {
        setPhase({ at: 'refused', ...refusal });
        return;
      }
      setPhase({ at: 'refused', text: `Couldn't read the card — ${textOf(err)}` });
    }
  }, [txid, network, gate]);

  useEffect(() => {
    if (open) void load();
    else gate.close();
    // Taken off the page (he went elsewhere mid-tap) ends the tap as closing does — as the v2 sheet's does (pinned
    // 30 Oct 2026: unmounted, a tap in flight still asked the Ledger and recorded its signature).
    return () => gate.close();
  }, [open, load, gate]);

  // One ceremony at a time, guarded SYNCHRONOUSLY before the first await (Screens and Astra, 27 Oct 2026, on the v2
  // twin): the card is read again before the device is asked, and the button disabled only once the phase said
  // 'signing' — two taps during a slow read started two ceremonies. A hook, so ABOVE the early return: the sheet
  // stays mounted and toggles `open`, and a hook after it threw "Rendered more hooks" on opening (Screens, 27 Oct).
  const inFlight = useRef(false);

  if (!open) return null;

  const check = 'check' in phase ? phase.check : null;
  const facts = check?.facts ?? null;
  const tail = facts ? facts.to.slice(-CONFIRM_TAIL) : '';
  // Typed for this card and this address — an ending typed for another card unlocks nothing here.
  const endingTyped = confirmationHolds(typedFor, { txid, to: facts?.to ?? null });
  const armed = !!check && (check.ready || (check.mustConfirm && endingTyped));

  function onType(value: string) {
    setTyped(value);
    if (!check || !facts || typedFor !== null || !check.mustConfirm) return;
    if (value.trim() !== tail) return;
    if (!check.anchor || !('kind' in check.anchor) || !('partyName' in check.anchor)) return;
    // He has typed the end of the address that is in the signed bytes. That
    // unlocks THIS signing and shows him the rest of it. Nothing is saved yet:
    // an address can be made to end the same way.
    // A desk wallet typed here (nothing the device could derive it from) is recorded under its layer.
    setTypedFor({ txid, address: facts.to, partyName: check.anchor.partyName, kind: check.anchor.kind, layer: check.anchor.kind === 'desk' ? check.destinationLayer : null });
  }

  async function sign() {
    if (inFlight.current) return;
    inFlight.current = true;
    try {
      await signOnce();
    } finally {
      inFlight.current = false;
    }
  }

  async function signOnce() {
    if (!check || !facts || !armed || !network || check.signer === null) return;
    // What this signing shows lands only while the sheet is still open for
    // this card: closed meanwhile, or opened for another, nothing of it shows
    // there. The device signs and the server records regardless.
    const live = gate.claim();
    const show = (p: Phase) => { if (live()) setPhase(p); };
    const cable = webHid();
    if (!cable) {
      show({ at: 'failed', check, text: 'This browser cannot reach a Ledger over a cable.', remedy: 'Use Brave or Edge on the computer the Ledger is plugged into, or sign on a phone from the link.', askedDevice: false });
      return;
    }
    // Re-read and re-checked at the tap, on this device's clock: the sheet
    // may have been open a while, and the bytes sent are the bytes checked.
    let fresh: SigningCard;
    try {
      fresh = await api.card(txid);
    } catch (err) {
      // A card that lapsed, was signed elsewhere or was replaced while the
      // sheet was open: the same sentence as on opening — nothing was signed.
      const refusal = cardRefusal(err);
      if (refusal) {
        show({ at: 'refused', ...refusal });
        void qc.invalidateQueries({ queryKey: ['custody', 'approvals'] });
        return;
      }
      show({ at: 'failed', check, text: `Couldn't read the card — ${textOf(err)}`, remedy: 'Nothing was signed. Close this and open the card again.', askedDevice: false });
      return;
    }
    const again = checkCard(fresh, { txid, network, now: Date.now(), confirmed: loadConfirmed(network) });
    // The confirmation is checked against the card as re-read, by txid and destination.
    const confirmed = confirmationHolds(typedFor, { txid, to: again.facts?.to ?? null });
    if (again.problems.length || again.signer === null || (!again.ready && !(again.mustConfirm && confirmed))) {
      show({ at: 'failed', check: again, text: again.problems[0] ?? 'The card no longer passes its checks.', remedy: 'Nothing was signed. Close this and open the card again.', askedDevice: false });
      return;
    }

    // Closed or replaced while the card was re-read: this tap is over — the device is never asked for it (Astra, max,
    // pre-live, 29 Oct 2026: `show` only stopped the painting, so a cancelled sheet's card still went to the Ledger
    // while another sheet was on screen, and its signature was recorded).
    if (!live()) return;
    let askedDevice = false;
    let deviceSigned = false;
    show({ at: 'signing', check: again, step: null, text: 'Starting…' });
    try {
      // The bytes this sheet decoded and showed — and nothing else — go to
      // the device; the signer is the key the server named; the derivation is
      // the register's path, tried first (then the account search), under the
      // Ledger account the card names for its key (slice E: a renewed Hot
      // address is account 1's, not account 0's) — a hint, never a widening:
      // the key found is held to the signer, and the server checks the same.
      const { signature } = await signOnLedger({
        hid: cable, signer: again.signer, rawDataHex: fresh.rawDataHex, network,
        derivation: again.derivation,
        account: fresh.source?.account ?? undefined,
        // A desk destination is derived on the device before it signs.
        prove: again.prove,
        onStep: (step, text) => {
          if (step === 'sign') askedDevice = true;
          show({ at: 'signing', check: again, step, text });
        },
      });
      deviceSigned = true;
      // Closed while the device was signing: the signature is dropped, never recorded — the card stays unsigned.
      if (!live()) return;
      show({ at: 'signing', check, step: 'sign', text: 'Recording the signature…' });
      const answer = await api.submitSignature(txid, signature);
      // The server took it: it was made by the device registered to this
      // operator, over these bytes. Only now does a destination go onto this
      // browser's list, under the name it was shown with — one whose ending
      // he typed. A desk wallet the device proved is recorded by nothing: the
      // proof is repeated on every card.
      const remember = confirmed ? typedFor : null;
      const remembered = remember === null || confirmDestination(network, remember);
      const notKept = remember === null || remembered ? ''
        : ' This browser would not save the destination, so it will ask you to confirm it again next time.';
      show({ at: 'signed', check, text: signedSentence(answer) + notKept });
      void qc.invalidateQueries({ queryKey: ['custody', 'approvals'] });
      void qc.invalidateQueries({ queryKey: ['custody', 'summary'] });
    } catch (err) {
      if (err instanceof ApiError) {
        const reason = typeof err.body.reason === 'string' ? err.body.reason : null;
        show({
          at: 'failed', check, askedDevice,
          text: signatureRefusal(err.status, reason),
          // An answer that gives its reason is a decision: the signature was
          // not recorded. One without may have come after it was.
          remedy: refusalIsDone(reason) ? null
            : reason !== null ? 'Your device did sign, and the desk did not record that signature: nothing will be sent with it.'
            : 'Your device did sign. Whether the desk recorded it is not known here — check the list before you try again.',
        });
        return;
      }
      show({
        at: 'failed', check, askedDevice,
        text: textOf(err),
        remedy: remedyOf(err) ?? (deviceSigned ? 'Your device did sign. Whether the desk recorded it is not known here — check the list before you try again.' : null),
      });
    }
  }

  const canConfirm = !!check?.mustConfirm;
  const banner = check?.anchor ? anchorSentence(check.anchor, canConfirm && !endingTyped) : null;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 p-0 sm:items-center sm:p-4">
      <div className="max-h-[92vh] w-full max-w-md overflow-y-auto rounded-t-2xl bg-tg-bg p-4 shadow-xl sm:rounded-2xl lg:bg-panel">
        <header className="mb-3 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="flex items-center gap-2 text-title font-semibold"><Usb className="h-4 w-4 text-tg-hint" /> {title}</h2>
          </div>
          <button onClick={onClose} aria-label="Close" className="flex min-h-[44px] min-w-[44px] shrink-0 items-center justify-center rounded-md text-tg-hint lg:min-h-[40px] lg:min-w-[40px]">
            <X className="h-4 w-4" />
          </button>
        </header>

        {phase.at === 'loading' && <Pending what="Reading…" />}

        {/* words:ok — the refusal is the signing page's own sentence (cardRefusal), shown whole */}
        {phase.at === 'refused' && <StateLine tone={phase.done ? 'good' : 'bad'}>{phase.text}</StateLine>}

        {/* The card's problems show whatever the facts: a card whose bytes are missing or will not decode has no facts, and its refusal is the one thing to show (Screens' Astra review, 22 Oct 2026). */}
        {/* words:ok — each problem is the card check's own sentence (sign-card.ts), shown whole */}
        {check && check.problems.map((p) => (
          <StateLine key={p} tone="bad" className="mb-2"><ShieldAlert className="mr-1 inline h-3.5 w-3.5" />{p}</StateLine>
        ))}
        {/* words:ok — the failure is the signing page's own sentence; its remedy sits behind Details */}
        {check && !facts && phase.at === 'failed' && (
          <StateLine tone="bad" className="mb-2" more={phase.remedy ?? (!phase.askedDevice ? 'This sheet has not asked your device to sign anything.' : undefined)}>{phase.text}</StateLine>
        )}

        {check && facts && (
          <>

            {/* words:ok — the anchor's sentence (sign-card.ts anchorSentence), shown whole */}
            {banner && !endingTyped && (
              <StateLine tone={banner.tone === 'ok' ? 'good' : banner.tone === 'notice' ? 'warn' : 'bad'} className="mb-2">
                {banner.tone === 'ok' ? <ShieldCheck className="mr-1 inline h-3.5 w-3.5" /> : <ShieldAlert className="mr-1 inline h-3.5 w-3.5" />}{banner.text}
              </StateLine>
            )}
            {endingTyped && typedFor && (
              <StateLine tone="good" className="mb-2" more="Compare the whole address below with your record, then again on the Ledger's screen.">
                The ending matches — now compare the whole address.
              </StateLine>
            )}

            {canConfirm && check.anchor && 'partyName' in check.anchor && 'kind' in check.anchor && (
              <div className={`mb-3 rounded-md border p-3 ${endingTyped ? 'border-stroke' : 'border-neg/50'}`}>
                <h3 className="text-body font-semibold">Confirm this address</h3>
                {/* words:ok — the typing instruction is the signing page's own (sign-card.ts confirmInstruction) */}
                <p className="mt-1 text-meta text-tg-hint">{confirmInstruction(check.anchor, CONFIRM_TAIL)}</p>
                <p className="mt-2 break-all font-mono text-body">
                  {facts.to.slice(0, -CONFIRM_TAIL)}<span className="text-tg-hint">{endingTyped ? tail : '•'.repeat(CONFIRM_TAIL)}</span>
                </p>
                <input
                  className={`${FIELD} mt-2 w-full font-mono`}
                  value={typed}
                  disabled={endingTyped}
                  maxLength={CONFIRM_TAIL}
                  placeholder={`last ${CONFIRM_TAIL} characters`}
                  autoComplete="off" autoCapitalize="off" spellCheck={false}
                  onChange={(e) => onType(e.target.value)}
                />
                {!endingTyped && <p className="mt-1 text-meta text-tg-hint">Sign opens when this matches.</p>}
              </div>
            )}

            <div className="card">
              {/* The move's word, never its code: a quarantine_move reads "Flagged move". */}
              <Label>{KIND_WORD[facts.kind] ?? 'Card'}{facts.network === 'nile' ? ' · Nile' : ''}</Label>
              <div className="font-mono text-figure font-semibold tabular">{groupDigits(facts.amount)} <span className="text-body font-normal text-tg-hint">USDT</span></div>
              <div className="mt-2 space-y-1 text-body">
                <div className="row"><span className="label">To</span><span className="font-medium">{check.anchor && 'partyName' in check.anchor ? check.anchor.partyName : 'no payable entry'}</span></div>
                <div className="row"><span className="label">Address</span><span className="break-all text-right font-mono text-meta">{endingTyped ? facts.to : facts.toShown}</span></div>
                <div className="row"><span className="label">From</span><span className="font-medium">{facts.fromName}</span></div>
                <div className="row"><span className="label">From address</span><span className="break-all text-right font-mono text-meta">{facts.from}</span></div>
                <div className="row"><span className="label">Fee limit</span><span className="font-mono">{groupDigits(facts.feeLimit)} TRX</span></div>
                {facts.closesAt !== null && (
                  <div className="row"><span className="label">Sign before</span><span>{fmtTime(facts.closesAt)} — {Math.max(0, Math.floor((facts.closesAt - Date.now()) / 60_000))} min left</span></div>
                )}
              </div>
            </div>

            {/* The Ledger's screen is the one display a compromised server cannot reach; this is what to compare there. */}
            <StateLine className="mt-3" more={facts.network === 'nile'
              ? <>On the test network the Ledger shows a <b>Custom Contract</b> call, not the amount: check <b>Contract</b> is the test USDT and <b>From</b> (after “P{facts.permission} -”) is the address above. Anything different: reject on the device.</>
              : <>On the Ledger, compare <b>Amount</b>, <b>To</b> (every character) and <b>From</b> (after “P{facts.permission} -”) with this sheet. Anything different: reject on the device.</>}>
              Compare the Ledger's screen with this sheet before confirming.
            </StateLine>

            {/* words:ok — the device's own step, as ledger-cable words it */}
            {phase.at === 'signing' && <StateLine className="mt-3">{phase.text}</StateLine>}
            {/* words:ok — the signed sentence is the signing page's own (refusals.ts signedSentence) */}
            {phase.at === 'signed' && <StateLine tone="good" className="mt-3"><ShieldCheck className="mr-1 inline h-3.5 w-3.5" />{phase.text}</StateLine>}
            {/* words:ok — the failure is the signing page's own sentence; its remedy sits behind Details */}
            {phase.at === 'failed' && (
              <StateLine tone="bad" className="mt-3" more={phase.remedy ?? (!phase.askedDevice ? 'This sheet has not asked your device to sign anything.' : undefined)}>{phase.text}</StateLine>
            )}

            <div className="mt-3 flex gap-2">
              {phase.at === 'signed' ? (
                <button className="btn-primary min-h-[44px] flex-1 text-body" onClick={onClose}>Done</button>
              ) : (
                <button
                  className="btn-primary flex min-h-[44px] flex-1 items-center justify-center gap-2 text-body"
                  disabled={!armed || phase.at === 'signing'}
                  onClick={() => void sign()}
                >
                  <Usb className="h-3.5 w-3.5" />
                  {phase.at === 'signing' ? 'On the Ledger…'
                    : phase.at === 'failed' ? 'Try again'
                    : check.problems.length ? 'Refusing to sign'
                    : canConfirm && !endingTyped ? 'Confirm the address to continue'
                    : !check.ready && !endingTyped ? 'Refusing to sign'
                    : 'Sign on Ledger'}
                </button>
              )}
              <button className="btn-secondary min-h-[44px] text-body" onClick={onClose}>{phase.at === 'signed' ? 'Close' : 'Cancel'}</button>
            </div>
            <p className="mt-2 text-meta text-tg-hint">Ledger Live closed, device unlocked, TRON app open.</p>
          </>
        )}
      </div>
    </div>
  );
}
